/**
 * VariantsTable Component
 * 
 * Displays A/B test variant performance comparison.
 */

import type { ABTestResults, VariantPerformance } from '@formflow/shared';
import './VariantsTable.css';

interface VariantsTableProps {
  data: ABTestResults | null;
  loading?: boolean;
}

export function VariantsTable({ data, loading }: VariantsTableProps) {
  if (loading) {
    return (
      <div className="variants-table-container">
        <div className="variants-header">
          <h3 className="variants-title">A/B Test Results</h3>
        </div>
        <div className="variants-loading">
          <div className="table-skeleton">
            <div className="skeleton-row" />
            <div className="skeleton-row" />
            <div className="skeleton-row" />
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.variants || data.variants.length === 0) {
    return (
      <div className="variants-table-container">
        <div className="variants-header">
          <h3 className="variants-title">A/B Test Results</h3>
        </div>
        <div className="variants-empty">
          <span className="empty-icon">🔬</span>
          <p>No A/B tests running for this form</p>
          <p className="empty-hint">Create variant steps in the form builder to start testing</p>
        </div>
      </div>
    );
  }

  // Find the control variant for comparison
  const controlVariant = data.variants.find((v: VariantPerformance) => v.is_control);
  const controlRate = controlVariant?.conversion_rate || 0;

  // Sort variants: control first, then by conversion rate
  const sortedVariants = [...data.variants].sort((a, b) => {
    if (a.is_control) return -1;
    if (b.is_control) return 1;
    return b.conversion_rate - a.conversion_rate;
  });

  return (
    <div className="variants-table-container">
      <div className="variants-header">
        <div>
          <h3 className="variants-title">A/B Test Results</h3>
          <p className="variants-subtitle">
            {data.variants.length} variants across {getUniqueSteps(data.variants)} steps
          </p>
        </div>
        {data.winner_variant_id && (
          <div className="winner-badge">
            <span className="winner-icon">🏆</span>
            <span>Winner detected</span>
          </div>
        )}
      </div>

      <div className="variants-table-wrapper">
        <table className="variants-table">
          <thead>
            <tr>
              <th>Variant</th>
              <th>Step</th>
              <th>Views</th>
              <th>Completions</th>
              <th>Conversion</th>
              <th>Lift vs Control</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {sortedVariants.map((variant: VariantPerformance) => {
              const lift = variant.is_control 
                ? null 
                : controlRate > 0 
                  ? ((variant.conversion_rate - controlRate) / controlRate * 100)
                  : 0;
              
              const isWinner = variant.variant_id === data.winner_variant_id;
              
              return (
                <tr 
                  key={variant.variant_id} 
                  className={`${variant.is_control ? 'control-row' : ''} ${isWinner ? 'winner-row' : ''}`}
                >
                  <td>
                    <div className="variant-name-cell">
                      {isWinner && <span className="winner-icon-small">🏆</span>}
                      <span className="variant-name">{variant.variant_name}</span>
                      {variant.is_control && (
                        <span className="control-badge">Control</span>
                      )}
                    </div>
                  </td>
                  <td>{variant.step_name}</td>
                  <td>{variant.views.toLocaleString()}</td>
                  <td>{variant.completions.toLocaleString()}</td>
                  <td>
                    <span className={`conversion-rate ${getConversionClass(variant.conversion_rate, controlRate, variant.is_control)}`}>
                      {variant.conversion_rate.toFixed(1)}%
                    </span>
                  </td>
                  <td>
                    {variant.is_control ? (
                      <span className="baseline">Baseline</span>
                    ) : (
                      <span className={`lift ${lift && lift > 0 ? 'positive' : 'negative'}`}>
                        {lift !== null && lift > 0 ? '+' : ''}
                        {lift !== null ? `${lift.toFixed(1)}%` : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    {variant.is_control ? (
                      <span className="baseline">—</span>
                    ) : variant.confidence_level !== undefined ? (
                      <ConfidenceIndicator 
                        level={variant.confidence_level} 
                        isSignificant={variant.is_significant}
                      />
                    ) : (
                      <span className="na">N/A</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="variants-footer">
        <p className="variants-note">
          💡 Statistical significance requires at least 100 visitors per variant. 
          Results above 95% confidence are considered significant.
        </p>
      </div>
    </div>
  );
}

interface ConfidenceIndicatorProps {
  level: number;
  isSignificant?: boolean;
}

function ConfidenceIndicator({ level, isSignificant }: ConfidenceIndicatorProps) {
  return (
    <div className="confidence-indicator">
      <div className="confidence-bar">
        <div 
          className={`confidence-fill ${isSignificant ? 'significant' : ''}`}
          style={{ width: `${Math.min(level, 100)}%` }}
        />
      </div>
      <span className={`confidence-value ${isSignificant ? 'significant' : ''}`}>
        {level.toFixed(0)}%
      </span>
    </div>
  );
}

function getUniqueSteps(variants: VariantPerformance[]): number {
  const uniqueSteps = new Set(variants.map(v => v.step_id));
  return uniqueSteps.size;
}

function getConversionClass(rate: number, controlRate: number, isControl: boolean): string {
  if (isControl) return '';
  if (rate > controlRate * 1.1) return 'above';
  if (rate < controlRate * 0.9) return 'below';
  return '';
}

export default VariantsTable;
